import * as React from 'react'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { Text } from '@amzn/dvui/dist/Text'
import { PlayBox } from '../playButton/PlayButton'
import { StringId } from '../localizer/stringId'
import { TextLocalizer } from '../localizer/localizer'
import { isLiveEntityType } from '../../utils/entityTypes'
import { getPlaybackClickstreamRef } from '../../utils/getPlaybackClickstreamRef'
import { getPlaybackProgress } from '../../utils/getPlaybackProgress'
import {
  getFirstPlaybackData,
  getPlaybackSecondaryMessages,
} from '../../utils/playbackActionHelper'
import * as st from './episodePlayback.module.scss'
import * as cn from 'classnames'
import { DecodedText } from '../decodedText/decodedText'
import { withLocalization, LocalizerProps } from '../localizer/localizer'
import { TransactionOverlay } from '../actionBox/disableOverlay'
import { widgetNameClasses } from '../../utils/widgetNameClasses'
import {
  epPurchasingText,
  isPurchasingEpisode,
  isPurchasingSeason,
} from '../../utils/actionBox'
import { ActionBoxTitleStatus } from '../../reducers/actionBox'
import { PurchaseOffer } from '../../selectors/purchase'
import { withMaybeSpaContext, MaybeSpaContextProps } from '@shared'
import { PlaybackContentLink } from '@shared'

export interface IPlaybackProps {
  sessionID: string
  title?: string
  isBonus?: boolean
  sequenceNumber?: number
  playbackActions?: WpsRuntime.atv.wps.PlaybackActionsModelProps
  notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]
  entityType?: string
  mainActionBox: ActionBoxTitleStatus | undefined
  epActionBox: ActionBoxTitleStatus | undefined
  useMFA: boolean
  usePlayer: boolean
  purchaseDetails: PurchaseOffer | null
  isRefunding: boolean
  isDeeplinkableActionReady: boolean
  startPlayback: (playbackContentLink: PlaybackContentLink) => void
}

export type EpTitleProps = IPlaybackProps & LocalizerProps & MaybeSpaContextProps

const getDeepLinkingData = (
  playbackAction: WpsRuntime.atv.wps.PlaybackActionDataProps,
  sessionID: string
) => ({
  fallbackUrl: playbackAction.fallbackURL,
  titleId: playbackAction.playbackID,
  resumeTime: playbackAction.resumeTime,
  signInUrl: playbackAction.signInURL,
  sessionId: sessionID,
  ref: getPlaybackClickstreamRef(
    playbackAction.contentType,
    playbackAction.entitlementType
  ),
  videoType: playbackAction.videoMaterialType,
})

const NotificationActionsMessage = ({
  notificationActions,
}: {
  notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]
}) => (
  <>
    {(
      notificationActions ||
      ([] as WpsRuntime.atv.wps.NotificationActionProps[])
    )
      .filter(
        ({ severity }) => !severity || ['CRITICAL', 'ERROR'].includes(severity)
      )
      .map(notification => (
        <span className={st.playbackNotifications}>
          <Text string={notification.message} />
        </span>
      ))}
  </>
)

const PlaybackActionMessages = ({
  messages,
}: {
  messages?: WpsRuntime.atv.wps.StringList
}) =>
  messages && messages.length > 0 ? (
    <span className={st.playbackNotifications}>
      {messages.map((message, idx) => (
        <PlaybackActionMessage key={idx} message={message} />
      ))}
    </span>
  ) : null

const PlaybackActionMessage = ({ message }: { message: String }) => (
  <span className={st.playbackMessage}>
    <Text string={message} />
  </span>
)

const EpTitle = ({
  title,
  sequenceNumber,
  isBonus,
  entityType,
}: {
  title?: string
  sequenceNumber?: number
  isBonus: boolean
  entityType?: string
}) => {
  if (!title) {
    return null
  }

  if (isLiveEntityType(entityType) || (!sequenceNumber && !isBonus)) {
    return <DecodedText string={title} />
  }

  if (isBonus) {
    return (
      <TextLocalizer
        stringID={StringId.DV_DP_EL_bonus_title_template}
        replace={{ episodeTitle: title }}
        shouldDecode={true}
      />
    )
  }

  return (
    <TextLocalizer
      stringID={StringId.DV_DP_EL_episode_title_template}
      replace={{
        sequenceNumber: sequenceNumber ? sequenceNumber.toString() : '',
        episodeTitle: title,
      }}
      shouldDecode={true}
    />
  )
}

const EpTitleWithMessages = (
  {
    title,
    useMFA,
    purchaseDetails,
    sequenceNumber,
    epActionBox,
    isBonus = false,
    entityType,
    playbackActions,
    notificationActions,
    inProgress
  }: EpTitleProps & {inProgress: boolean}
) => (
  <div className={`dv-episode-noplayback-title ${st.playboxUnavailable}`}>
    <div
      className={cn(
        {
          [st.inProgress]: inProgress,
          [st.purchasing]: useMFA
            ? Boolean(purchaseDetails)
            : isPurchasingEpisode(epActionBox),
        },
        st.title
      )}
    >
      <EpTitle
        title={title}
        sequenceNumber={sequenceNumber}
        isBonus={isBonus}
        entityType={entityType}
      />
    </div>
    <NotificationActionsMessage notificationActions={notificationActions} />
    <PlaybackActionMessages
      messages={getPlaybackSecondaryMessages(playbackActions)}
    />
  </div>
)

const EpTitleWithPlayback = ({
  title,
  playbackActions,
  sessionID,
  sequenceNumber,
  isBonus = false,
  entityType,
  getString,
  usePlayer,
  hasDownloadCompleted,
  playOffline,
  startPlayback
}: EpTitleProps) => {
  const playbackData = getFirstPlaybackData(playbackActions)

  if (!playbackData) {
    return null
  }

  const progress = getPlaybackProgress(playbackData.progress)
  const deeplinkingData = getDeepLinkingData(playbackData, sessionID)

  // build user-friendly accessibility description
  const ariaText = title
    ? sequenceNumber
      ? getString(StringId.DV_DP_ARIA_watch_episode, {
          episodeNumber: sequenceNumber.toString(),
          episodeTitle: title,
        })
      : getString(StringId.DV_DP_ARIA_watch_title, { title: title })
    : undefined // fallback to use the not-that-user-friendly text description

  const playbackTitleId = playbackData.playbackID
  const downloadCompleted =
    hasDownloadCompleted && hasDownloadCompleted(playbackTitleId)

  const triggerOnlinePlayback = (e: React.MouseEvent) => {
    const contentConfig = {
      asin: playbackTitleId,
      trailer: false,
      position: playbackData.resumeTime,
      ref: getPlaybackClickstreamRef(
        playbackData.contentType,
        playbackData.entitlementType
      ),
      videoType: playbackData.videoMaterialType,
      useFullView: true,
    }

    startPlayback({
      contentConfig,
      signInUrl: playbackData.signInURL,
      playbackUrl: playbackData.playbackURL,
    })
    e.preventDefault()
  }

  const triggerOfflinePlayback = (e: React.MouseEvent) => {
    playOffline!(playbackTitleId)
    e.preventDefault()
  }

  const handler = downloadCompleted
    ? triggerOfflinePlayback
    : triggerOnlinePlayback

  return (
    <div className={`dv-episode-playback-title ${st.playbox}`}>
      <PlayBox
        progress={progress}
        deepLinkingData={deeplinkingData}
        ariaLabel={ariaText}
        href={playbackData.playbackURL}
        onClick={usePlayer ? handler : undefined}
      >
        <div className={`js-episode-title-name ${st.playboxActionLabel}`}>
          <EpTitle
            title={title}
            sequenceNumber={sequenceNumber}
            isBonus={isBonus}
            entityType={entityType}
          />
        </div>
      </PlayBox>
    </div>
  )
}

export const EpisodePlayback: React.ComponentType<IPlaybackProps> = withMaybeSpaContext(
  withLocalization((props: EpTitleProps) => {
      const {
        playbackActions,
        useMFA,
        purchaseDetails,
        epActionBox,
        mainActionBox,
        isRefunding,
        isDeeplinkableActionReady
      } = props
      const playbackData = getFirstPlaybackData(playbackActions)
      const hasPlayback = Boolean(playbackData)

      const getPurchasingText = () => {
        if (hasPlayback) {
          return ''
        }
        return useMFA
          ? (purchaseDetails &&
              purchaseDetails.details &&
              purchaseDetails.details.offer.purchaseData.text) ||
              ''
          : epPurchasingText(epActionBox)
      }

      const inProgress =
        (useMFA
          ? Boolean(purchaseDetails)
          : isPurchasingEpisode(epActionBox) ||
            isPurchasingSeason(mainActionBox)) || isRefunding

      return (
        <div className={`js-eplist-episode ${widgetNameClasses.playback}`}>
          {isDeeplinkableActionReady && playbackData ? (
            <EpTitleWithPlayback {...props} />
          ) : (
            <EpTitleWithMessages {...props} inProgress={inProgress} />
          )}
          {inProgress && (
            <TransactionOverlay
              className={st.progressWrapper}
              text={getPurchasingText()}
              useSmallSpinner
            />
          )}
        </div>
      )
  })
)